import React from 'react';
import { Box, Container, Typography, Card, CardContent, Chip, Button, List, ListItem, ListItemIcon, ListItemText, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useServices } from '../context/ServicesContext';

const ServicesSection = ({data}) => {
    const IMG_PATH = process.env.REACT_APP_IMAGE_BASEURL || "";
    const { t } = useTranslation();
    const theme = useTheme();
    const { services } = useServices();

    const isLoading = data === undefined;

    return (
        <Box
            id="services"
            sx={{
                py: 10,
                backgroundColor: theme.palette.background.service,
            }}
        >
            <Container maxWidth="xl">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Chip
                        label={t('pages.home.services.chip_title')}
                        sx={{
                            backgroundColor: theme.palette.background.chip,
                            color: theme.palette.custom.chipText,
                            fontWeight: 300,
                            fontSize: '1rem',
                            mb: 2,
                            px: 2
                        }}
                    />
                    <Typography variant="h3" component="h2" fontWeight="bold" sx={{
                        color: theme.typography.h3.color || '#3784b4ff',
                        textAlign: 'center',
                        width: { xs: '100%', sm: '80%', md: '60%' },
                        margin: '0 auto',
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                        display: 'flex',
                        justifyContent: 'center',
                    }}>
                        {isLoading ? (
                            <Skeleton variant="text" height={60} width="80%" />
                        ) : (
                            data?.meta?.title || t('pages.home.services.headline')
                        )}
                    </Typography>
                    <Typography variant="h6" fontWeight="light" color={theme.palette.text.secondary} sx={{ 
                        mb: 4, 
                        mt: 4, 
                        px: { xs: 2, md: 0 } 
                    }}>
                        {isLoading ? (
                            <Skeleton variant="text" height={30} width="100%" />
                        ) : (
                            data?.meta?.description || t('pages.home.services.subheadline')
                        )}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                        },
                        gap: 4,
                    }}
                >
                    {services.map((service) => (
                        <Card
                            key={service._id}
                            sx={{
                                height: '100%',
                                borderRadius: '16px',
                                boxShadow: theme.palette.custom.cardShadow,
                                transition: 'all 0.3s ease-in-out',
                                overflow: 'hidden',
                                border: '1px solid transparent',
                                '&:hover': {
                                    boxShadow: theme.palette.custom.cardShadowHover,
                                    borderColor: theme.palette.primary.main,
                                    '& .hover-overlay': {
                                        opacity: 1,
                                    },
                                    '& .service-icon': {
                                        transform: 'scale(1.15)',
                                    },
                                },
                            }}
                        >
                            <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box
                                    className="service-icon"
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: '12px',
                                        background: theme.palette.custom.serviceIconBg,
                                        color: theme.palette.custom.serviceIconColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 3,
                                        transition: 'transform 0.3s ease-in-out',
                                    }}
                                >
                                    {service.icon && (
                                        <Box
                                            component="img"
                                            src={service?.image ? IMG_PATH + service.image : "/empty-icon.svg"}
                                            alt={service.title}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                objectFit: 'contain',
                                            }}
                                        />
                                    )}
                                </Box>

                                <Typography variant="h5" component="h3" fontWeight="medium" sx={{ mb: 2 }}>
                                    {service.title}
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {service.description}
                                </Typography>

                                <List disablePadding sx={{ mb: 2 }}>
                                    {service.features.slice(0, 3).map((point, index) => (
                                        <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 24 }}>
                                                <FiberManualRecordIcon sx={{ fontSize: 8, color: theme.palette.primary.main }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={point}
                                                slotProps={{ typography: { variant: 'body2', color: 'text.secondary', fontWeight: 500 } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>

                                <Button
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        px: 0,
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {t('pages.home.services.learn_more')}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

export default ServicesSection;
